<template>
    <a-modal
        :visible="visible"
        :footer="null"
        :width="560"
        centered
        destroy-on-close
        @cancel="$emit('close')"
    >
        <template slot="title">
            <span class="wpp-title">
                <i class="fab fa-whatsapp wpp-title-icon"></i>
                WhatsApp do pedido #{{ order && order.id }}
            </span>
        </template>

        <a-empty
            v-if="!messages.length"
            description="Nenhuma mensagem de WhatsApp registrada para este pedido."
        />

        <div v-else class="wpp-list">
            <section v-for="message in messages" :key="message.id" class="wpp-item">
                <div class="wpp-item-hd">
                    <strong>{{ reasonLabel(message.reason) }}</strong>
                    <span class="wpp-status" :title="status(message.status).hint">
                        <span class="material-icons-round" :class="'wpp-tone-' + status(message.status).tone">
                            {{ status(message.status).icon }}
                        </span>
                        <span :class="'wpp-tone-' + status(message.status).tone">{{ status(message.status).label }}</span>
                    </span>
                </div>

                <div class="wpp-to">Para {{ formatPhone(message.phone) }}</div>

                <div class="wpp-steps">
                    <span><span class="wpp-muted">Enviada:</span> {{ formatTime(message.sent_at || message.created_at) }}</span>
                    <span><span class="wpp-muted">Entregue:</span> {{ formatTime(message.delivered_at) }}</span>
                    <span><span class="wpp-muted">Lida:</span> {{ formatTime(message.read_at) }}</span>
                </div>

                <a-alert
                    v-if="message.status === 'failed'"
                    type="error"
                    show-icon
                    message="A mensagem não foi entregue."
                    :description="message.error || undefined"
                />

                <p v-if="message.body" class="wpp-bubble">{{ message.body }}</p>
            </section>
        </div>
    </a-modal>
</template>

<script>
import { whatsappStatus } from "./order-whatsapp";

const REASON = {
    ORDER_INITIAL: "Aviso de novo pedido",
    REORDER_INITIAL: "Reenvio do pedido",
    ORDER_CANCEL: "Aviso de cancelamento",
};

/**
 * Mensagens de WhatsApp enviadas ao vendedor por causa do pedido (kyojin
 * `whatsapp_messages`), da mais antiga à mais nova.
 */
export default {
    name: "OrderWhatsappModal",
    props: {
        visible: { type: Boolean, default: false },
        order: { type: Object, default: null },
    },
    computed: {
        messages() {
            return (this.order && this.order.whatsapp_messages) || [];
        },
    },
    methods: {
        status: whatsappStatus,
        reasonLabel(reason) {
            return REASON[reason] || reason || "Mensagem";
        },
        // O kyojin manda o horário de Brasília sem fuso ("Y-m-d H:i:s"): lido do texto.
        formatTime(value) {
            if (!value) return "–";
            const [date = "", time = ""] = String(value).split(/[T ]/);
            const [year, month, day] = date.split("-");
            return day && month && year ? `${day}/${month}/${year} ${time.slice(0, 5)}`.trim() : value;
        },
        formatPhone(phone) {
            const nums = String(phone || "").replace(/\D/g, "").replace(/^55(?=\d{10,11}$)/, "");
            if (nums.length === 11) return nums.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
            if (nums.length === 10) return nums.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
            return phone || "–";
        },
    },
};
</script>

<style lang="scss" scoped>
.wpp-title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
}
.wpp-title-icon {
    color: #25d366;
    font-size: 1.2rem;
}
.wpp-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
}
.wpp-item {
    border: 1px solid #f0f0f0;
    border-radius: 8px;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
}
.wpp-item-hd {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}
.wpp-status {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-weight: 500;
    .material-icons-round {
        font-size: 1rem;
    }
}
.wpp-to,
.wpp-muted {
    color: rgba(0, 0, 0, 0.45);
    font-size: 12px;
}
.wpp-steps {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
    font-size: 12px;
    font-variant-numeric: tabular-nums;
}
.wpp-bubble {
    background: #f5f5f5;
    border-radius: 8px;
    padding: 10px 12px;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    font-size: 13px;
    line-height: 1.5;
    margin: 0;
}
.wpp-tone-ok {
    color: #30aa4c;
}
.wpp-tone-info {
    color: #1677ff;
}
.wpp-tone-neutral {
    color: rgba(0, 0, 0, 0.65);
}
.wpp-tone-pending {
    color: orange;
}
.wpp-tone-error {
    color: #cd2a2a;
}
.wpp-tone-muted {
    color: rgba(0, 0, 0, 0.35);
}
</style>
